import React, { useState, useEffect, useCallback } from 'react';
import { 
    Row, Col, Card, Typography, Select, Button, Table, Badge, Tag, 
    Spin, message, DatePicker, Space, Empty, List, Tooltip, Flex, 
    Statistic, Avatar, Input, Modal, Collapse
} from 'antd';
import { 
    TeamOutlined, CheckCircleOutlined, SyncOutlined, 
    AlertOutlined, UserAddOutlined, DownloadOutlined,
    CalendarOutlined, UserOutlined, EnvironmentOutlined,
    GlobalOutlined, InfoCircleOutlined, MessageOutlined,
    ReloadOutlined, SearchOutlined,
    AuditOutlined, StarFilled, RocketOutlined, HistoryOutlined, 
    WarningOutlined, CompassOutlined, MedicineBoxOutlined, ClockCircleOutlined, CrownOutlined,
    StopOutlined
} from '@ant-design/icons';
import { 
    BarChart, Bar, XAxis, YAxis, CartesianGrid, 
    Tooltip as ChartTooltip, ResponsiveContainer, Legend, 
    PieChart, Pie, Cell, ReferenceLine 
} from 'recharts';
import axios from 'axios';
import dayjs from 'dayjs';
import DoctorHistoryModal from '../../components/DoctorHistoryModal';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const baseUrl = 'https://preprod-admin.babyworld.tn/api/v1';
const getConfig = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});
const PIE_COLORS = ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#13c2c2', '#eb2f96'];

// =========================================================================
// ⚡ CONFIGURATION DU PROFIL DU MÉDECIN (100% HOMOGÈNE AVEC RAPPORTS) ⚡
// =========================================================================
export const RELATIONSHIP_CONFIG = {
  PARTNER_ACTIVE: {
    label: "Partenaire Stratégique",
    tagColor: "gold",
    icon: <StarFilled />,
    bgColor: "#fffbe6",
    borderColor: "#fadb14",
    titleColor: "#856404",
    shadow: "0 4px 12px rgba(250, 219, 20, 0.18)"
  },
  PARTNER_AT_RISK: {
    label: "Partenaire Sous Vigilance",
    tagColor: "warning",
    icon: <WarningOutlined />,
    bgColor: "#fff7e6",
    borderColor: "#fa8c16",
    titleColor: "#d46b08",
    shadow: "0 4px 12px rgba(250, 140, 22, 0.18)"
  },
  KEY_PRESCRIBER: {
    label: "Prescripteur Clé (A+)",
    tagColor: "success",
    icon: <RocketOutlined />,
    bgColor: "#f6ffed",
    borderColor: "#52c41a",
    titleColor: "#237804",
    shadow: "0 4px 12px rgba(82, 196, 26, 0.18)"
  },
  STANDARD: {
    label: "Standard",
    tagColor: "default",
    icon: <UserOutlined />,
    bgColor: "#f0f5ff",
    borderColor: "#adc6ff",
    titleColor: "#002766",
    shadow: "none"
  },
  REFRACTORY: {
    label: "Non Réceptif",
    tagColor: "error",
    icon: <StopOutlined />,
    bgColor: "#fff1f0",
    borderColor: "#ff4d4f",
    titleColor: "#cf1322",
    shadow: "0 4px 12px rgba(255, 77, 79, 0.18)"
  }
};

const getCategoryKey = (record) => {
    const raw = String(record?.relationshipCategory || record?.prescriptionProfile || '').toUpperCase().trim();
    if (raw === 'REFRACTORY' || raw.includes('RÉCEPTIF') || raw.includes('RECEPTIF')) return 'REFRACTORY';
    if (raw === 'PARTNER_AT_RISK' || raw.includes('VIGILANCE')) return 'PARTNER_AT_RISK';
    if (raw === 'KEY_PRESCRIBER' || raw.includes('A+')) return 'KEY_PRESCRIBER';
    if (record?.isPartner || raw === 'PARTNER_ACTIVE') return 'PARTNER_ACTIVE';
    return 'STANDARD';
};

// --- COMPOSANT TOOLTIP PERSONNALISÉ (BARRES) ---
const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        const data = payload[0].payload;

        return (
            <div style={{ 
                backgroundColor: '#fff', 
                border: '1px solid #d9d9d9', 
                padding: '12px 14px', 
                borderRadius: '8px', 
                boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
                width: '320px',
                pointerEvents: 'none'
            }}>
                <p style={{ fontWeight: 'bold', fontSize: '13px', margin: '0 0 6px 0', color: '#1f1f1f' }}>
                    {data.fullDate || label}
                </p>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span style={{ color: '#1890ff', fontWeight: 'bold' }}>
                        Total: {data.visites || data.realized} visites
                    </span>
                    {data.planned !== undefined && (
                        <span style={{ fontSize: '12px', color: '#8c8c8c' }}>
                            🎯 Obj: {data.planned}
                        </span>
                    )}
                </div>

                {data.regions && data.regions.length > 0 && (
                    <div style={{ marginTop: '8px', borderTop: '1px solid #f0f0f0', paddingTop: '8px' }}>
                        <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#ff4d4f', marginBottom: '6px' }}>
                            📍 RÉGIONS COUVERTES ({data.regions.length}) :
                        </div>

                        <div style={{ 
                            maxHeight: '280px',
                            overflowY: 'hidden',
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(2, 1fr)', 
                            gap: '4px 8px' 
                        }}>
                            {data.regions.map((r, i) => (
                                <div 
                                    key={i} 
                                    style={{ 
                                        backgroundColor: '#fafafa',
                                        borderRadius: '4px',
                                        padding: '2px 6px',
                                        fontSize: '10px',
                                        color: '#262626',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        border: '1px solid #f0f0f0'
                                    }}
                                    title={typeof r === 'string' ? r : r.name}
                                >
                                    • {typeof r === 'string' ? r : r.name}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    }
    return null;
};

const DashboardPage = () => {
    const [loading, setLoading] = useState(false);
    const [kpis, setKpis] = useState(null);
    const [rankings, setRankings] = useState([]);
    const [dailyTrend, setDailyTrend] = useState([]);
    const [delegates, setDelegates] = useState([]);
    const [regionAnalysis, setRegionAnalysis] = useState({});
    const [selectedRegion, setSelectedRegion] = useState(null);
    const [alerts, setAlerts] = useState([]); 
    const [regionAlerts, setRegionAlerts] = useState([]);
    const [regionSearch, setRegionSearch] = useState('');

    const [doctorModalVisible, setDoctorModalVisible] = useState(false);
    const [selectedDoctorId, setSelectedDoctorId] = useState(null);

    const handleOpenDoctorHistory = (personId) => {
        setSelectedDoctorId(personId);
        setDoctorModalVisible(true);
    };

    const [selectedBarDelegate, setSelectedBarDelegate] = useState(null);

    const [dailyMonitoring, setDailyMonitoring] = useState(null);
    const [loadingDaily, setLoadingDaily] = useState(false);

    const [historyModalVisible, setHistoryModalVisible] = useState(false);
    const [historyDate, setHistoryDate] = useState(dayjs().subtract(2, 'days'));
    const [historyData, setHistoryData] = useState(null);
    const [loadingHistory, setLoadingHistory] = useState(false);
    
    const [selectedDelegate, setSelectedDelegate] = useState(null);
    const [dateRange, setDateRange] = useState([dayjs().startOf('month'), dayjs()]);
    const [selectedCategory, setSelectedCategory] = useState('all');

    const [prevWeekData, setPrevWeekData] = useState(null);
    const [loadingPrevWeek, setLoadingPrevWeek] = useState(false);

    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [modalDetails, setModalDetails] = useState({ title: '', type: '', list: [] });

    const [offPlanModalVisible, setOffPlanModalVisible] = useState(false);
    const [selectedOffPlanInfo, setSelectedOffPlanInfo] = useState({ region: '', delegateName: '', doctors: [] });

    const [prevWeekHistoryModalVisible, setPrevWeekHistoryModalVisible] = useState(false);
    const [historyWeekRange, setHistoryWeekRange] = useState([
        dayjs().subtract(2, 'week').day(1),
        dayjs().subtract(2, 'week').day(7)
    ]);
    const [historyWeekData, setHistoryWeekData] = useState(null);
    const [loadingHistoryWeek, setLoadingHistoryWeek] = useState(false);

    const [supervisorModalVisible, setSupervisorModalVisible] = useState(false);
    const [selectedSupervisorVisits, setSelectedSupervisorVisits] = useState({ delegateName: '', visits: [] });

    const handleOpenSupervisorVisits = (delegateName, visits) => {
        setSelectedSupervisorVisits({ delegateName, visits: visits || [] });
        setSupervisorModalVisible(true);
    };

    const fetchHistoryWeekCompliance = async (range) => {
        if (!range || !range[0] || !range[1]) return;
        setLoadingHistoryWeek(true);
        try {
            const startStr = range[0].format('YYYY-MM-DD');
            const endStr = range[1].format('YYYY-MM-DD');
            const catParam = selectedCategory && selectedCategory !== 'all' ? `&category=${selectedCategory}` : '';
            const delParam = selectedDelegate ? `&delegateId=${selectedDelegate}` : '';

            const res = await axios.get(
                `${baseUrl}/dashboard/previous-week-compliance?startDate=${startStr}&endDate=${endStr}${catParam}${delParam}`, 
                getConfig()
            );
            setHistoryWeekData(res.data);
        } catch (e) {
            message.error("Erreur lors du chargement de l'historique de la semaine.");
        } finally {
            setLoadingHistoryWeek(false);
        }
    };

    const [frequentVisitsAlerts, setFrequentVisitsAlerts] = useState([]);

    const getCategoryTagColor = (cat) => {
        if (!cat) return 'default';
        const c = String(cat).toUpperCase();
        if (c === 'AKTIV') return 'blue';
        if (c === 'SANOSAN') return 'green';
        if (c === 'MATERNITE') return 'purple';
        return 'cyan';
    };

    const findDelegateCategory = useCallback((item) => {
        if (item.category) return item.category;
        if (item.delegateCategory) return item.delegateCategory;
        const clean = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
        const found = delegates.find(d => {
            if (item.delegateId && d.id === item.delegateId) return true;
            if (item.delegateName) {
                const d1 = clean(`${d.firstname}${d.lastname}`);
                const d2 = clean(`${d.lastname}${d.firstname}`);
                const target = clean(item.delegateName);
                return target === d1 || target === d2 || (clean(d.lastname) && target.includes(clean(d.lastname)));
            }
            return false;
        });
        return found?.category || null;
    }, [delegates]);

    useEffect(() => {
        const fetchFilters = async () => {
            try {
                const res = await axios.get(`${baseUrl}/delegates?minimal=true`, getConfig());
                setDelegates(res.data || []);
            } catch (e) { 
                console.error("Erreur delegates:", e); 
            }
        };
        fetchFilters();
    }, []);

    const fetchDailyMonitoring = useCallback(async () => {
        setLoadingDaily(true);
        try {
            let params = new URLSearchParams();
            if (selectedCategory && selectedCategory !== 'all') params.append('category', selectedCategory);
            if (selectedDelegate) params.append('delegateId', selectedDelegate);
            const query = params.toString() ? `?${params.toString()}` : '';

            const res = await axios.get(`${baseUrl}/dashboard/daily-monitoring${query}`, getConfig());
            setDailyMonitoring(res.data);
        } catch (e) {
            console.error("Erreur chargement monitoring J-1", e);
        } finally {
            setLoadingDaily(false);
        }
    }, [selectedCategory, selectedDelegate]);

    const fetchHistoryMonitoring = async (date) => {
        if (!date) return;
        setLoadingHistory(true);
        try {
            const catParam = selectedCategory && selectedCategory !== 'all' ? `&category=${selectedCategory}` : '';
            const delParam = selectedDelegate ? `&delegateId=${selectedDelegate}` : '';
            const res = await axios.get(
                `${baseUrl}/dashboard/daily-monitoring?date=${date.format('YYYY-MM-DD')}${catParam}${delParam}`, 
                getConfig()
            );
            setHistoryData(res.data);
        } catch (e) {
            message.error("Erreur lors de la récupération de l'historique.");
        } finally {
            setLoadingHistory(false);
        }
    };

    useEffect(() => {
        fetchDailyMonitoring();
    }, [fetchDailyMonitoring]);

    const fetchPrevWeekCompliance = useCallback(async () => {
        setLoadingPrevWeek(true);
        try {
            let params = new URLSearchParams();
            if (selectedCategory && selectedCategory !== 'all') params.append('category', selectedCategory);
            if (selectedDelegate) params.append('delegateId', selectedDelegate);
            const query = params.toString() ? `?${params.toString()}` : '';

            const res = await axios.get(`${baseUrl}/dashboard/previous-week-compliance${query}`, getConfig());
            setPrevWeekData(res.data);
        } catch (e) {
            console.error("Erreur chargement conformité semaine précédente", e);
        } finally {
            setLoadingPrevWeek(false);
        }
    }, [selectedCategory, selectedDelegate]);

    useEffect(() => {
        fetchPrevWeekCompliance();
    }, [fetchPrevWeekCompliance]);

    const handleOpenDetails = (delegateName, type, list, currentCycleInfo = null) => {
        setModalDetails({
            title: type === 'missed' 
                ? `Médecins Manqués - ${delegateName}` 
                : `Médecins Hors Portefeuille Visités - ${delegateName}`,
            type,
            list,
            delegateName,
            currentCycleInfo
        });
        setDetailModalVisible(true);
    };

    const prevWeekColumns = [
        {
            title: 'Délégué',
            dataIndex: 'delegateName',
            key: 'delegateName',
            width: 220,
            render: (name) => <Text strong><UserOutlined style={{ marginRight: 6 }} />{name}</Text>
        },
        {
            title: 'Visites Réalisées',
            dataIndex: 'totalVisits',
            key: 'totalVisits',
            align: 'center',
            width: 170,
            render: (count, record) => (
                <Space direction="vertical" size={3} align="center" style={{ width: '100%' }}>
                    <Tag color="blue" style={{ fontWeight: 'bold', margin: 0 }}>
                        {count} visite{count > 1 ? 's' : ''}
                    </Tag>
                    {record.leaves && record.leaves.length > 0 && (
                        record.leaves.map((l, idx) => (
                            <Tooltip key={idx} title={`Motif : ${l.reason} (du ${l.start} au ${l.end})`}>
                                <Tag color="warning" style={{ fontSize: '11px', fontWeight: 'bold', margin: 0, cursor: 'help' }}>
                                    🌴 Congé ({l.start} - {l.end})
                                </Tag>
                            </Tooltip>
                        ))
                    )}
                    {record.holidays && record.holidays.length > 0 && (
                        record.holidays.map((h, idx) => (
                            <Tooltip key={idx} title={`Jour férié : ${h.name} le ${h.date}`}>
                                <Tag color="purple" style={{ fontSize: '10px', margin: 0, cursor: 'help' }}>
                                    🎉 Férié : {h.name}
                                </Tag>
                            </Tooltip>
                        ))
                    )}
                </Space>
            )
        },
        {
            title: 'Visites Manquées (Semaine N-1)',
            dataIndex: 'missedCount',
            key: 'missedCount',
            align: 'center',
            width: 220,
            render: (count, record) => (
                count > 0 ? (
                    <Button 
                        danger 
                        size="small" 
                        onClick={() => handleOpenDetails(record.delegateName, 'missed', record.missedDoctors, record.currentCycleInfo)}
                    >
                        ⚠️ {count} médecin{count > 1 ? 's' : ''} manqué{count > 1 ? 's' : ''}
                    </Button>
                ) : (
                    <Tag color="success" icon={<CheckCircleOutlined />}>Aucun manqué</Tag>
                )
            )
        },
        {
            title: 'Médecins Hors Planning Semaine N-1',
            dataIndex: 'unassignedCount',
            key: 'unassignedCount',
            align: 'center',
            width: 250,
            render: (count, record) => (
                count > 0 ? (
                    <Button 
                        type="primary" 
                        ghost 
                        size="small" 
                        style={{ borderColor: '#fa8c16', color: '#fa8c16' }}
                        onClick={() => handleOpenDetails(record.delegateName, 'unassigned', record.unassignedDoctors, record.currentCycleInfo)}
                    >
                        ⭐ {count} hors planning visité{count > 1 ? 's' : ''}
                    </Button>
                ) : (
                    <Tag color="default" style={{ color: '#52c41a' }}>0 (100% Conforme au Planning)</Tag>
                )
            )
        },
        {
            title: 'Contrôle Superviseur',
            dataIndex: 'supervisorVisitsCount',
            key: 'supervisorVisitsCount',
            align: 'center',
            width: 190,
            render: (count, record) => (
                count > 0 ? (
                    <Button 
                        size="small" 
                        style={{ 
                            backgroundColor: '#f9f0ff', 
                            borderColor: '#d3adf7', 
                            color: '#722ed1',
                            fontWeight: 'bold' 
                        }}
                        onClick={() => handleOpenSupervisorVisits(record.delegateName, record.supervisorVisits)}
                    >
                        👑 {count} contrôle{count > 1 ? 's' : ''} / acc.
                    </Button>
                ) : (
                    <Text type="secondary" style={{ fontSize: '12px' }}>Aucun</Text>
                )
            )
        }
    ];

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const config = { headers: { Authorization: `Bearer ${token}` } };

            let params = new URLSearchParams();
            if (selectedDelegate) params.append('delegateId', selectedDelegate);
            if (selectedCategory && selectedCategory !== 'all') params.append('category', selectedCategory);
            
            if (dateRange && dateRange[0] && dateRange[1]) {
                const sStr = dayjs(dateRange[0]).format('YYYY-MM-DD');
                const eStr = dayjs(dateRange[1]).format('YYYY-MM-DD');
                params.append('startDate', sStr);
                params.append('endDate', eStr);
                // ⚡ Ajout du paramètre dateInterval de secours au cas où le contrôleur utilise l'un ou l'autre
                params.append('dateInterval', `${sStr},${eStr}`);
            }

            // ⚡ Ajout d'un cache-buster pour empêcher le navigateur de renvoyer une réponse en cache 304 Not Modified
            params.append('_t', Date.now().toString());

            const query = params.toString();
            console.log(">>> [DASHBOARD] Envoi de la requête avec les paramètres :", query);

            const [
                resKpi, resRank, resTrend, resRegion, resAlerts, resRegionAlerts, resFrequentAlerts
            ] = await Promise.all([
                axios.get(`${baseUrl}/dashboard/kpis?${query}`, config),                
                axios.get(`${baseUrl}/dashboard/rankings?${query}`, config),             
                axios.get(`${baseUrl}/dashboard/daily-trend?${query}`, config),           
                axios.get(`${baseUrl}/dashboard/region-analysis?${query}`, config),       
                axios.get(`${baseUrl}/dashboard/alerts/missing-visits?${query}`, config), 
                axios.get(`${baseUrl}/dashboard/alerts/missing-regions?${query}`, config),
                axios.get(`${baseUrl}/dashboard/alerts/frequent-visits?${query}`, config)
            ]);
            
            console.log(">>> [DASHBOARD] Réponse KPI reçue du Backend :", resKpi.data);
            console.log(">>> [DASHBOARD] Réponse Rankings reçue :", resRank.data);

            setKpis(resKpi.data);
            setRankings(resRank.data || []);
            setDailyTrend(resTrend.data || []);
            setRegionAnalysis(resRegion.data || {});
            setAlerts(resAlerts.data || []);
            setRegionAlerts(resRegionAlerts.data || []); 
            setFrequentVisitsAlerts(resFrequentAlerts.data || []);

        } catch (error) { 
            console.error(">>> [DASHBOARD ERROR] Erreur chargement dashboard:", error);
            message.error("Erreur lors du chargement des statistiques du Dashboard.");
        } finally { 
            setLoading(false); 
        }
    }, [selectedDelegate, dateRange, selectedCategory]);

    useEffect(() => { 
        loadData(); 
    }, [loadData]);

    // =========================================================================
    // ⚡ OUVERTURE DU PARCOURS MÉDECIN AVEC RÉCUPÉRATION DU PROFIL EXACT ⚡
    // =========================================================================
    


    const handleExportFrequentVisits = () => {
        if (!frequentVisitsAlerts || frequentVisitsAlerts.length === 0) {
            message.warning("Aucune visite rapprochée à exporter.");
            return;
        }

        const headers = [
            "Médecin", "Spécialité", "Région", "Partenaire", "Nombre Visites",
            "Écart Total", "Type Alerte", "Délégués Impliqués", "Visite N°",
            "Date et Heure Visite", "Délégué Visiteur", "Cycle Actif",
            "Semaine Cycle", "Période Semaine", "Statut Planning"
        ];

        const rows = [];

        frequentVisitsAlerts.forEach(item => {
            const alertType = item.isSameDelegate 
                ? `Même délégué (${item.delegatesNamesList?.[0] || item.delegate1Name})`
                : `Délégués différents (${item.uniqueDelegatesCount || 2})`;

            const delegatesList = item.delegatesNamesList ? item.delegatesNamesList.join(' & ') : `${item.delegate1Name} & ${item.delegate2Name}`;

            (item.visits || []).forEach(v => {
                let planningStatus = "Hors cycle actif";
                let cycleName = "N/A";
                let weekStr = "N/A";
                let periodStr = "N/A";

                if (v.context) {
                    cycleName = v.context.cycleName || "N/A";
                    weekStr = `Semaine ${v.context.weekNumber}`;
                    periodStr = `${v.context.startShort} au ${v.context.endShort}`;
                    if (v.context.isPlannedThisWeek) {
                        planningStatus = "Prévu cette semaine";
                    } else if (v.context.plannedInOtherWeek) {
                        planningStatus = `Prévu en Semaine ${v.context.plannedInOtherWeek}`;
                    } else {
                        planningStatus = "Hors planning cycle";
                    }
                }

                rows.push([
                    `"${item.name || ''}"`,
                    `"${item.specialtyName || ''}"`,
                    `"${item.regionName || ''}"`,
                    item.isPartner ? "OUI" : "NON",
                    item.totalVisitsCount || 2,
                    `"${item.totalSpanDays || item.daysDiff}"`,
                    `"${alertType}"`,
                    `"${delegatesList}"`,
                    `Visite ${v.visitIndex || 1}`,
                    `"${v.dateFull || ''}"`,
                    `"${v.delegateName || ''}"`,
                    `"${cycleName}"`,
                    `"${weekStr}"`,
                    `"${periodStr}"`,
                    `"${planningStatus}"`
                ]);
            });
        });

        const csvContent = [headers.join(";"), ...rows.map(r => r.join(";"))].join("\n");
        const blob = new Blob(["\ufeff" + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `visites_rapprochees_${dayjs().format('YYYY-MM-DD')}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
        message.success("Export des visites rapprochées téléchargé avec succès !");
    };

    const filteredRegions = Object.keys(regionAnalysis).filter(name =>
        name.toLowerCase().includes(regionSearch.toLowerCase().trim())
    );

    const kpiCardStyle = { 
        textAlign: 'center', borderRadius: 12, height: '130px', 
        display: 'flex', flexDirection: 'column', justifyContent: 'center',
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: 'none' 
    };

    const monitoringColumns = [
        {
            title: 'Délégué',
            dataIndex: 'delegateName',
            key: 'delegateName',
            width: 200,
            render: (name) => <Text strong><UserOutlined style={{ marginRight: 6 }} />{name}</Text>
        },
        {
            title: 'Rapports Déposés',
            dataIndex: 'reportsCount',
            key: 'reportsCount',
            align: 'center',
            width: 140,
            render: (count) => (
                <Tag color={count > 0 ? "blue" : "default"} style={{ fontSize: '13px', fontWeight: 'bold' }}>
                    {count} visite{count > 1 ? 's' : ''}
                </Tag>
            )
        },
        {
            title: 'Régions Visitées (Prévues)',
            dataIndex: 'inPlanRegions',
            key: 'inPlanRegions',
            render: (regions) => (
                regions && regions.length > 0 ? (
                    <Space wrap size={[4, 4]}>
                        {regions.map((r, i) => (
                            <Tag key={i} color="success" icon={<CheckCircleOutlined />}>{r}</Tag>
                        ))}
                    </Space>
                ) : <Text type="secondary">-</Text>
            )
        },
        {
            title: 'Régions HORS Planning Semaine',
            dataIndex: 'offPlanRegions',
            key: 'offPlanRegions',
            render: (offRegions, record) => (
                offRegions && offRegions.length > 0 ? (
                    <Space wrap size={[4, 4]}>
                        {offRegions.map((r, i) => {
                            const count = record.offPlanDetails?.[r]?.length || 0;
                            return (
                                <Tooltip key={i} title="Cliquez pour voir les médecins visités et leur vrai planning">
                                    <Tag 
                                        color="error" 
                                        icon={<WarningOutlined />} 
                                        style={{ fontWeight: 'bold', cursor: 'pointer', padding: '2px 8px' }}
                                        onClick={() => {
                                            setSelectedOffPlanInfo({
                                                region: r,
                                                delegateName: record.delegateName,
                                                currentCycleInfo: record.currentCycleInfo, 
                                                doctors: record.offPlanDetails?.[r] || []
                                            });
                                            setOffPlanModalVisible(true);
                                        }}
                                    >
                                        {r} ({count}) 🔍
                                    </Tag>
                                </Tooltip>
                            );
                        })}
                    </Space>
                ) : <Tag color="default" style={{ color: '#52c41a' }}>Aucune (Conforme)</Tag>
            )
        },
        {
            title: 'Contrôle Superviseur',
            dataIndex: 'supervisorVisitsCount',
            key: 'supervisorVisitsCount',
            align: 'center',
            width: 190,
            render: (count, record) => (
                count > 0 ? (
                    <Button 
                        size="small" 
                        style={{ 
                            backgroundColor: '#f9f0ff', 
                            borderColor: '#d3adf7', 
                            color: '#722ed1',
                            fontWeight: 'bold' 
                        }}
                        onClick={() => handleOpenSupervisorVisits(record.delegateName, record.supervisorVisits)}
                    >
                        👑 {count} contrôle{count > 1 ? 's' : ''} J-1
                    </Button>
                ) : (
                    <Text type="secondary" style={{ fontSize: '12px' }}>Aucun</Text>
                )
            )
        }
    ];

    const formattedDailyTrend = React.useMemo(() => {
        if (!dailyTrend || dailyTrend.length === 0) return [];

        const list = [];
        for (let i = 0; i < dailyTrend.length; i++) {
            const current = dailyTrend[i];
            list.push(current);

            const next = dailyTrend[i + 1];
            if (next) {
                const curDay = String(current.day || '').toLowerCase();
                const nextDay = String(next.day || '').toLowerCase();

                const isFri = curDay.includes('fri') || curDay.includes('ven');
                const isMon = nextDay.includes('mon') || nextDay.includes('lun');

                if (isFri && isMon) {
                    list.push({
                        day: 'Weekend',
                        fullDate: 'Weekend (Samedi & Dimanche)',
                        visites: 0,
                        regions: [],
                        delegates: []
                    });
                }
            }
        }

        return list.map(item => {
            const d = String(item.day || '').toLowerCase().trim();
            if (d === '' || d.includes('sam') || d.includes('dim') || d.includes('sat') || d.includes('sun')) {
                return { ...item, day: 'Weekend', fullDate: 'Weekend (Samedi & Dimanche)', visites: 0 };
            }
            return item;
        });
    }, [dailyTrend]);

    return (
        <div className="dashboard-container" style={{ padding: '25px', background: '#f0f2f5', minHeight: '100vh' }}>
            
            <style>{`
                .recharts-tooltip-wrapper {
                    z-index: 9999 !important;
                    pointer-events: auto !important;
                }
                .search-region-input input {
                    height: 36px !important;
                    line-height: 36px !important;
                }
                .custom-tooltip-scroll::-webkit-scrollbar {
                    width: 5px;
                }
                @media (max-width: 768px) {
                    .dashboard-container {
                        padding: 12px 8px !important;
                    }
                    .kpi-responsive-card {
                        height: auto !important;
                        min-height: 95px !important;
                        padding: 8px 6px !important;
                    }
                    .kpi-responsive-card .ant-statistic-title {
                        font-size: 10px !important;
                        line-height: 1.2 !important;
                        white-space: normal !important;
                        margin-bottom: 4px !important;
                    }
                    .kpi-responsive-card .ant-statistic-content {
                        font-size: 16px !important;
                    }
                    .kpi-responsive-card .ant-statistic-content-value {
                        font-size: 16px !important;
                    }
                    .ant-collapse-header {
                        flex-wrap: wrap !important;
                        align-items: flex-start !important;
                        padding: 10px 12px !important;
                    }
                    .ant-collapse-header .ant-collapse-header-text {
                        flex: 1 1 100% !important;
                        margin-bottom: 8px !important;
                    }
                    .ant-collapse-header .ant-collapse-extra {
                        width: 100% !important;
                        margin-left: 0 !important;
                        display: flex !important;
                        justify-content: flex-start !important;
                        flex-wrap: wrap !important;
                        gap: 6px !important;
                    }
                    .ant-collapse-header .ant-collapse-extra .ant-space {
                        width: 100% !important;
                        flex-wrap: wrap !important;
                    }
                    .mobile-alert-list .ant-list-item {
                        flex-direction: column !important;
                        align-items: flex-start !important;
                        position: relative !important;
                        padding: 12px 4px !important;
                    }
                    .mobile-alert-list .ant-list-item-meta {
                        width: 100% !important;
                        max-width: 100% !important;
                        margin: 0 !important;
                    }
                    .mobile-alert-list .ant-list-item-action {
                        margin-left: 0 !important;
                        margin-top: 8px !important;
                        width: 100% !important;
                        display: flex !important;
                        justify-content: flex-end !important;
                    }
                }
            `}</style>

            {/* --- HEADER & FILTRES --- */}
            <div style={{ marginBottom: 25, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                <div>
                    <Title level={2} style={{ margin: 0 }}>Tableau de Bord Intelligence</Title>
                    <Text type="secondary">Analyse de la performance et couverture géographique</Text>
                </div>
                <Button type="primary" icon={<ReloadOutlined />} onClick={loadData} loading={loading}>Actualiser</Button>
            </div>

            {/* --- FILTRES GLOBAUX --- */}
            <Card style={{ marginBottom: 25, borderRadius: 12, border: 'none' }}>
                <Row gutter={16} align="bottom">
                    <Col xs={24} sm={12} md={6}>
                        <Text strong style={{ display: 'block', marginBottom: 8 }}>Période :</Text>
                        <RangePicker style={{ width: '100%' }} value={dateRange} onChange={setDateRange} format="DD/MM/YYYY" />
                    </Col>

                    <Col xs={24} sm={12} md={5}>
                        <Text strong style={{ display: 'block', marginBottom: 8 }}>Gamme :</Text>
                        <Select 
                            style={{ width: '100%' }} 
                            value={selectedCategory} 
                            onChange={(val) => {
                                setSelectedCategory(val);
                                setSelectedDelegate(null);
                            }}
                        >
                            <Option value="all">Toutes les gammes</Option>
                            <Option value="AKTIV"><Tag color="blue" style={{ margin: 0, fontWeight: 'bold' }}>AKTIV</Tag></Option>
                            <Option value="SANOSAN"><Tag color="green" style={{ margin: 0, fontWeight: 'bold' }}>SANOSAN</Tag></Option>
                            <Option value="MATERNITE"><Tag color="purple" style={{ margin: 0, fontWeight: 'bold' }}>MATERNITE</Tag></Option>
                        </Select>
                    </Col>

                    <Col xs={24} sm={12} md={6}>
                        <Text strong style={{ display: 'block', marginBottom: 8 }}>Délégué :</Text>
                        <Select 
                            showSearch
                            optionFilterProp="label"
                            style={{ width: '100%' }} 
                            placeholder="Tous les délégués"
                            value={selectedDelegate}
                            onChange={setSelectedDelegate}
                            allowClear
                        >
                            {delegates
                                .filter(d => !d.excludeFromStats)
                                .filter(d => selectedCategory === 'all' || d.category === selectedCategory)
                                .map(d => (
                                    <Option key={d.id} value={d.id} label={`${d.firstname} ${d.lastname}`}>
                                        {d.firstname} {d.lastname} {d.category ? `(${d.category})` : ''}
                                    </Option>
                                ))
                            }
                        </Select>
                    </Col>

                    <Col xs={24} sm={12} md={24} lg={7} className="filter-actions-col">
                        <Space wrap>
                            <Button type="primary" icon={<ReloadOutlined />} onClick={loadData} loading={loading}>
                                Actualiser
                            </Button>
                            <Button icon={<DownloadOutlined />}>Rapport Complet</Button>
                        </Space>
                    </Col>
                </Row>
            </Card>

            <Spin spinning={loading} size="large">
                {/* --- KPI ROW --- */}
               <Row gutter={[8, 8]} style={{ marginBottom: 20 }}>
                    <Col xs={12} sm={12} md={8} lg={5} xl={5}>
                        <Card className="kpi-responsive-card" style={kpiCardStyle} hoverable>
                            <Statistic 
                                title="VISITES / CIBLE" 
                                value={`${kpis?.visitsYesterday ?? 0} / ${kpis?.totalPlanned ?? 0}`} 
                                prefix={<TeamOutlined style={{ color: '#1890ff' }} />} 
                            />
                        </Card>
                    </Col>
                   <Col xs={12} sm={12} md={8} lg={5} xl={5}>
                        <Card className="kpi-responsive-card" style={kpiCardStyle} hoverable>
                            <Statistic title="TAUX RÉALISATION" value={kpis?.realizationRate || 0} suffix="%" valueStyle={{ color: '#52c41a' }} />
                        </Card>
                    </Col>
                    <Col xs={12} sm={12} md={8} lg={4} xl={4}>
                        <Card className="kpi-responsive-card" style={kpiCardStyle} hoverable>
                            <Statistic title="INDEX RETOUR" value={kpis?.returnIndex || 0} prefix={<SyncOutlined spin={loading} style={{ color: '#faad14' }} />} />
                        </Card>
                    </Col>
                   <Col xs={12} sm={12} md={8} lg={5} xl={5}>
                        <Card className="kpi-responsive-card" style={kpiCardStyle} hoverable>
                            <Statistic title="VISITES RATÉES" value={kpis?.missedVisitsWeek || 0} valueStyle={{ color: '#f5222d' }} />
                        </Card>
                    </Col>
                      <Col xs={24} sm={12} md={8} lg={5} xl={5}>
                        <Card className="kpi-responsive-card" style={kpiCardStyle} hoverable>
                            <Statistic title="NOUVELLES CRÉATIONS" value={kpis?.newCreations || 0} prefix={<UserAddOutlined style={{ color: '#722ed1' }} />} />
                        </Card>
                    </Col>
                </Row>

                {/* --- CONTRÔLE SEMAINE PRÉCÉDENTE --- */}
                <Row gutter={24} style={{ marginBottom: 25 }}>
                    <Col span={24}>
                        <Collapse
                            defaultActiveKey={['1']}
                            style={{ 
                                borderRadius: 12, 
                                border: 'none', 
                                backgroundColor: '#ffffff',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.04)' 
                            }}
                            items={[
                                {
                                    key: '1',
                                    label: (
                                        <Space>
                                            <MedicineBoxOutlined style={{ color: '#f5222d', fontSize: '18px' }} />
                                            <span style={{ fontWeight: 'bold', fontSize: '15px' }}>
                                                Contrôle Semaine Précédente ({prevWeekData?.periodLabel}) : Visites Manquées & Visites Hors Planning
                                            </span>
                                        </Space>
                                    ),
                                    extra: (
                                        <Space>
                                            <Button 
                                                type="primary" 
                                                ghost 
                                                size="small" 
                                                icon={<HistoryOutlined />} 
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setPrevWeekHistoryModalVisible(true);
                                                    fetchHistoryWeekCompliance(historyWeekRange);
                                                }}
                                            >
                                                Historique des semaines
                                            </Button>
                                            <Button 
                                                size="small" 
                                                icon={<ReloadOutlined />} 
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    fetchPrevWeekCompliance();
                                                }}
                                            >
                                                Actualiser
                                            </Button>
                                        </Space>
                                    ),
                                    children: (
                                        <Table 
                                            dataSource={prevWeekData?.delegates || []}
                                            columns={prevWeekColumns}
                                            rowKey="delegateId"
                                            loading={loadingPrevWeek}
                                            pagination={false}
                                            size="middle"
                                            scroll={{ x: 900, y: 350 }} 
                                            locale={{ emptyText: "Aucune donnée disponible pour la semaine passée." }}
                                        />
                                    )
                                }
                            ]}
                        />
                    </Col>
                </Row>

                {/* --- ACTIVITÉ J-1 & RÉGIONS HORS PLANNING --- */}
                <Row gutter={24} style={{ marginBottom: 25 }}>
                    <Col span={24}>
                        <Collapse
                            defaultActiveKey={['1']}
                            style={{ 
                                borderRadius: 12, 
                                border: 'none', 
                                backgroundColor: '#ffffff',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.04)' 
                            }}
                            items={[
                                {
                                    key: '1',
                                    label: (
                                        <Space>
                                            <CompassOutlined style={{ color: '#1890ff', fontSize: '18px' }} />
                                            <span style={{ fontWeight: 'bold', fontSize: '15px' }}>
                                                {dailyMonitoring?.isFridayShift 
                                                    ? `Activité du dernier jour ouvré (Vendredi : ${dailyMonitoring?.displayDate}) & Respect du Planning`
                                                    : `Activité de la veille (J-1 : ${dailyMonitoring?.displayDate || dayjs().subtract(1, 'day').format('DD/MM/YYYY')}) & Respect du Planning`
                                                }
                                            </span>
                                        </Space>
                                    ),
                                    extra: (
                                        <Button 
                                            type="primary" 
                                            ghost 
                                            size="small"
                                            icon={<HistoryOutlined />} 
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setHistoryModalVisible(true);
                                                fetchHistoryMonitoring(historyDate);
                                            }}
                                        >
                                            Historique des jours précédents
                                        </Button>
                                    ),
                                    children: (
                                        <Table 
                                            dataSource={dailyMonitoring?.delegates || []}
                                            columns={monitoringColumns}
                                            rowKey="delegateId"
                                            loading={loadingDaily}
                                            pagination={false}
                                            size="middle"
                                            scroll={{ x: 850, y: 350 }}
                                            locale={{ emptyText: "Aucune donnée enregistrée hier." }}
                                        />
                                    )
                                }
                            ]}
                        />
                    </Col>
                </Row>

                {/* --- ANALYSE RÉGIONALE --- */}
                <Row gutter={24} style={{ marginBottom: 25 }}>
                    <Col span={24}>
                        <Card 
                            title={<Space><GlobalOutlined style={{ color: '#1890ff' }} /> <span>Analyse Profonde : Spécialités visitées par Région</span></Space>}
                            style={{ borderRadius: 12, border: 'none' }}
                        >
                            <Row gutter={40}>
                                <Col xs={24} lg={8} style={{ borderRight: '1px solid #f0f0f0' }}>
                                    <div style={{ marginBottom: 10 }}>
                                        <Text strong type="secondary">Villes visitées sur la période choisie :</Text>
                                    </div>
                                    
                                    <div style={{ marginBottom: 15 }}>
                                        <Input.Search
                                            className="search-region-input"
                                            placeholder="Filtrer une région / ville..."
                                            allowClear
                                            value={regionSearch}
                                            onChange={(e) => setRegionSearch(e.target.value)}
                                            style={{ width: '100%' }}
                                        />
                                    </div>

                                    <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
                                        <List
                                            size="small"
                                            dataSource={filteredRegions}
                                            locale={{ emptyText: regionSearch ? "Aucune région ne correspond" : "Aucune donnée géographique" }}
                                            renderItem={name => (
                                                <List.Item 
                                                    style={{ 
                                                        cursor: 'pointer', 
                                                        border: 'none',
                                                        borderRadius: 8,
                                                        marginBottom: 5,
                                                        background: selectedRegion === name ? '#e6f7ff' : 'transparent',
                                                        padding: '10px 15px'
                                                    }}
                                                    onClick={() => setSelectedRegion(name)}
                                                >
                                                    <Flex justify="space-between" align="center" style={{ width: '100%' }}>
                                                        <Space>
                                                            <EnvironmentOutlined style={{ color: selectedRegion === name ? '#1890ff' : '#d9d9d9' }} />
                                                            <Text strong={selectedRegion === name}>{name}</Text>
                                                        </Space>
                                                        <Badge 
                                                            count={regionAnalysis[name]?.reduce((acc, curr) => acc + curr.count, 0) || 0} 
                                                            style={{ backgroundColor: selectedRegion === name ? '#1890ff' : '#bfbfbf' }} 
                                                        />
                                                    </Flex>
                                                </List.Item>
                                            )}
                                        />
                                    </div>
                                </Col>

                                <Col xs={24} lg={16}>
                                    {selectedRegion && regionAnalysis[selectedRegion] ? (
                                        <div style={{ textAlign: 'center' }}>
                                            <Title level={4} style={{ marginBottom: 20 }}>
                                                Répartition des spécialités : <span style={{ color: '#1890ff' }}>{selectedRegion.toUpperCase()}</span>
                                            </Title>
                                            <ResponsiveContainer width="100%" height={380}>
                                                <PieChart>
                                                    <Pie
                                                        data={regionAnalysis[selectedRegion]}
                                                        cx="50%" cy="50%"
                                                        innerRadius={80}
                                                        outerRadius={130}
                                                        paddingAngle={5}
                                                        dataKey="count"
                                                        nameKey="specialty"
                                                        label={({ specialty, percent }) => `${specialty} ${(percent * 100).toFixed(0)}%`}
                                                    >
                                                        {regionAnalysis[selectedRegion].map((entry, index) => (
                                                            <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                                                        ))}
                                                    </Pie>
                                                    <ChartTooltip />
                                                    <Legend verticalAlign="bottom" align="center" />
                                                </PieChart>
                                            </ResponsiveContainer>
                                        </div>
                                    ) : (
                                        <div style={{ padding: '100px 0' }}>
                                            <Empty description="Sélectionnez une région à gauche pour voir l'analyse détaillée" />
                                        </div>
                                    )}
                                </Col>
                            </Row>
                        </Card>
                    </Col>
                </Row>

                {/* --- RANKING & TREND --- */}
                <Row gutter={[16, 16]} style={{ marginBottom: 25 }}>
                    <Col xs={24} xl={15} style={{ position: 'relative', zIndex: 10 }}>
                        <Card title="Comparatif Réalisé vs Objectif" style={{ borderRadius: 12, border: 'none', position: 'relative', zIndex: 10 }}>
                            <ResponsiveContainer width="100%" height={350}>
                                <BarChart data={rankings}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis 
                                        dataKey="name" 
                                        interval={0} 
                                        angle={-45} 
                                        textAnchor="end" 
                                        height={90} 
                                        tick={{ fontSize: 9 }} 
                                    />
                                    <YAxis />
                                    <ChartTooltip 
                                        content={<CustomTooltip />}  
                                        wrapperStyle={{ pointerEvents: 'auto' }} 
                                        allowEscapeViewBox={{ x: true, y: true }}  
                                        offset={-5}                               
                                    />
                                    <Legend verticalAlign="top" align="right" />
                                    <Bar name="Cible (Planning)" dataKey="planned" fill="#7f8183" radius={[4, 4, 0, 0]} />
                                    <Bar name="Fait (Visites)" dataKey="realized" fill="#1890ff" radius={[4, 4, 0, 0]} style={{ cursor: 'pointer' }} onClick={(entry) => setSelectedBarDelegate(entry)} />
                                </BarChart>
                            </ResponsiveContainer>
                        </Card>
                    </Col>
                    <Col xs={24} xl={9}>
                        <Card title="Classement des Scores (40/30/30)" style={{ borderRadius: 10 }}>
                            <Table 
                                dataSource={rankings} 
                                pagination={false} 
                                size="small" 
                                rowKey="delegateId" 
                                scroll={{ y: 350 }}
                                columns={[
                                { 
                                    title: <Text strong style={{ color: '#000' }}>Délégué</Text>, 
                                    dataIndex: 'name', 
                                    key: 'name', 
                                    fixed: 'left',
                                    width: 150,
                                    render: (t, r, i) => (
                                        <span style={{ fontSize: '11px', fontWeight: 'bold' }}>
                                            {i + 1}. {t} {i === 0 && '🏆'}
                                        </span>
                                    ) 
                                },
                                { 
                                    title: (
                                        <Tooltip 
                                            placement="top"
                                            title={
                                                <div>
                                                    <b>VOLUME (Score / 40)</b><br/>
                                                    <i>Mesure la quantité de travail réalisé.</i><br/>
                                                    <span style={{ color: '#faad14' }}>Équation : (Visites / (Jours ouvrés × Obj/Jour)) × 40</span>
                                                </div>
                                            }
                                        >
                                            <Space style={{ cursor: 'help' }}>
                                                <span style={{ color: '#000', fontWeight: 'bold' }}>Vol.</span>
                                                <InfoCircleOutlined style={{ fontSize: '10px', color: '#1890ff' }} />
                                            </Space>
                                        </Tooltip>
                                    ), 
                                    dataIndex: 'volPoints', 
                                    align: 'center', 
                                    width: 70,
                                    render: (v) => <Text strong style={{ color: '#1890ff' }}>{v}</Text> 
                                },
                                { 
                                    title: (
                                        <Tooltip 
                                            placement="top"
                                            title={
                                                <div>
                                                    <b>RIGUEUR (Score / 30)</b><br/>
                                                    <i>Mesure le respect du planning établi.</i><br/>
                                                    <span style={{ color: '#faad14' }}>Équation : (% de médecins prévus visités) × 0.3</span>
                                                </div>
                                            }
                                        >
                                            <Space style={{ cursor: 'help' }}>
                                                <span style={{ color: '#000', fontWeight: 'bold' }}>Taux</span>
                                                <InfoCircleOutlined style={{ fontSize: '10px', color: '#52c41a' }} />
                                            </Space>
                                        </Tooltip>
                                    ), 
                                    dataIndex: 'tauxPoints', 
                                    align: 'center', 
                                    width: 75,
                                    render: (t) => <Text strong style={{ color: '#52c41a' }}>{t}</Text> 
                                },
                                { 
                                    title: (
                                        <Tooltip 
                                            placement="top"
                                            title={
                                                <div>
                                                    <b>IMPACT (Score / 30)</b><br/>
                                                    <i>Mesure la répétition et le suivi.</i><br/>
                                                    <span style={{ color: '#faad14' }}>Équation : (Total Visites / Total Assignés) × 30</span>
                                                </div>
                                            }
                                        >
                                            <Space style={{ cursor: 'help' }}>
                                                <span style={{ color: '#000', fontWeight: 'bold' }}>Idx</span>
                                                <InfoCircleOutlined style={{ fontSize: '10px', color: '#faad14' }} />
                                            </Space>
                                        </Tooltip>
                                    ), 
                                    dataIndex: 'idxPoints', 
                                    align: 'center', 
                                    width: 65,
                                    render: (i) => <Text strong style={{ color: '#faad14' }}>{i}</Text> 
                                },
                                { 
                                    title: <Text strong style={{ color: '#000' }}>Total</Text>, 
                                    dataIndex: 'totalScore', 
                                    align: 'right', 
                                    width: 90,
                                    render: (s) => (
                                        <Tag color="blue" style={{ fontWeight: 'bold', borderRadius: '4px', margin: 0, width: '100%', textAlign: 'center' }}>
                                            {s} / 100
                                        </Tag>
                                    ) 
                                }
                            ]}
                            />
                        </Card>
                    </Col>
                </Row>

                {/* --- GRAPHIQUE ÉVOLUTION QUOTIDIENNE --- */}
                <Row gutter={24} style={{ marginBottom: 25 }}>
                    <Col span={24}>
                        <Card title="Activité journalière de l'équipe (5 derniers jours)" style={{ borderRadius: 10 }}>
                            <div style={{ width: '100%', height: 300 }}>
                                <ResponsiveContainer>
                                    <BarChart data={formattedDailyTrend}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                        <XAxis 
                                            dataKey="day" 
                                            interval={0} 
                                            tick={{ fontSize: 10 }}
                                            tickFormatter={(val) => {
                                                if (!val) return '';
                                                return String(val).replace(/\s*\([a-zA-Z]+\)/, '');
                                            }}
                                        />
                                        <YAxis width={25} />
                                        <ChartTooltip content={<CustomTooltip />} cursor={{ fill: '#f8f9fa' }} />

                                    <ReferenceLine 
                                        x="04/09 (Fri)" 
                                        stroke="#fa8c16" 
                                        strokeDasharray="4 4" 
                                        strokeWidth={2}
                                        label={{ 
                                            value: ' Weekend ', 
                                            position: 'top', 
                                            fill: '#fa8c16', 
                                            fontSize: 12, 
                                            fontWeight: 'bold',
                                            offset: 10
                                        }} 
                                    />

                                    <Bar name="Visites totales" dataKey="visites" fill="#52c41a" radius={[4, 4, 0, 0]} barSize={45} />
                                </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </Card>
                    </Col>
                </Row>

                {/* --- BLOC DES 3 ALERTES CÔTE À CÔTE --- */}
                <Row gutter={16} style={{ marginBottom: 25 }}>
                    {/* 1. Alerte Médecins Partenaires */}
                   <Col xs={24} lg={8}>
                        <Card 
                            title={
                                <Space>
                                    <AlertOutlined style={{ color: '#f5222d' }} />
                                    <span style={{ color: '#f5222d', fontWeight: 'bold', fontSize: '13px' }}>
                                        Partenaires non visités (30j+)
                                    </span>
                                    <Badge count={alerts.length} style={{ backgroundColor: '#f5222d' }} />
                                </Space>
                            }
                            style={{ borderRadius: 12, border: '1px solid #ffa39e', background: '#fff2f0', height: '100%' }}
                        >
                            {alerts.length > 0 ? (
                                <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
                                    <List
                                        className="mobile-alert-list"
                                        size="small"
                                        dataSource={alerts}
                                        renderItem={item => {
                                            const catKey = getCategoryKey(item);
                                            const conf = RELATIONSHIP_CONFIG[catKey] || RELATIONSHIP_CONFIG.PARTNER_ACTIVE;

                                            return (
                                                <List.Item
                                                    actions={[
                                                        <Button 
                                                            size="small" 
                                                            type="primary" 
                                                            ghost
                                                            icon={<AuditOutlined />}
                                                            onClick={() => handleOpenDoctorHistory(item.id, item)}
                                                        >
                                                            Parcours
                                                        </Button>
                                                    ]}
                                                >
                                                    <List.Item.Meta
                                                        avatar={
                                                            <Avatar 
                                                                icon={<UserOutlined />} 
                                                                style={{ backgroundColor: '#ffccc7', color: '#f5222d', cursor: 'pointer' }} 
                                                                onClick={() => handleOpenDoctorHistory(item.id, item)}
                                                            />
                                                        }
                                                        title={
                                                            <Space wrap align="center" size={[4, 4]}>
                                                                <Text strong style={{ cursor: 'pointer', color: '#1890ff', fontSize: '13px' }} onClick={() => handleOpenDoctorHistory(item.id, item)}>
                                                                    {item.name}
                                                                </Text>
                                                                <Tag color={conf.tagColor} style={{ fontSize: '10px', fontWeight: 'bold', margin: 0, padding: '0 6px' }}>
                                                                    {conf.icon} {conf.label.toUpperCase()}
                                                                </Tag>
                                                                {item.delegates?.map((delName, idx) => (
                                                                    <Tag key={idx} color="blue" style={{ fontSize: '10px', margin: 0 }}>{delName}</Tag>
                                                                ))}
                                                            </Space>
                                                        }
                                                        description={
                                                            <Space wrap size={[4, 2]} style={{ marginTop: 4 }}>
                                                                <Tag color="error" style={{ fontSize: '10px', margin: 0 }}>
                                                                    {item.lastVisit === 'Aucune visite' ? 'Jamais visité' : item.lastVisit}
                                                                </Tag>
                                                                <Text type="secondary" style={{ fontSize: '11px' }}>
                                                                    {item.daysInactive >= 999 ? '(Aucune couverture)' : `(${item.daysInactive} j)`}
                                                                </Text>
                                                            </Space>
                                                        }
                                                    />
                                                </List.Item>
                                            );
                                        }}
                                    />
                                </div>
                            ) : (
                                <Empty description="Tous vos partenaires sont à jour !" />
                            )}
                        </Card>
                    </Col>

                    {/* 2. Alerte Régions Délaissées en 2026 */}
                    <Col xs={24} lg={8}>
                        <Card 
                            title={
                                <Space>
                                    <EnvironmentOutlined style={{ color: '#fa8c16' }} />
                                    <span style={{ color: '#d46b08', fontWeight: 'bold', fontSize: '13px' }}>
                                        Régions non couvertes en 2026 (30j+)
                                    </span>
                                    <Badge count={regionAlerts.length} style={{ backgroundColor: '#fa8c16' }} />
                                </Space>
                            }
                            style={{ borderRadius: 12, border: '1px solid #ffd591', background: '#fffbe6', height: '100%' }}
                        >
                            {regionAlerts.length > 0 ? (
                                <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
                                    <List
                                        size="small"
                                        dataSource={regionAlerts}
                                        renderItem={item => (
                                            <List.Item>
                                                <List.Item.Meta
                                                    avatar={
                                                        <Avatar 
                                                            icon={<EnvironmentOutlined />} 
                                                            style={{ backgroundColor: '#ffe7ba', color: '#fa8c16' }} 
                                                        />
                                                    }
                                                    title={<Text strong style={{ fontSize: '13px' }}>{item.name}</Text>}
                                                    description={
                                                        <Space align="center">
                                                            <Tag color="warning" style={{ fontSize: '10px' }}>
                                                                {item.lastVisit}
                                                            </Tag>
                                                            <Text type="secondary" style={{ fontSize: '11px' }}>
                                                                {item.daysInactive >= 999 ? '(Aucun passage en 2026)' : `(${item.daysInactive} j sans passage)`}
                                                            </Text>
                                                        </Space>
                                                    }
                                                />
                                            </List.Item>
                                        )}
                                    />
                                </div>
                            ) : (
                                <Empty description="Toutes les régions sont couvertes en 2026 !" />
                            )}
                        </Card>
                    </Col>

                    {/* 3. Visites Rapprochées / Doublons < 5j */}
                    <Col xs={24} lg={8}>
                        <Card 
                            title={
                                <Space>
                                    <ClockCircleOutlined style={{ color: '#c41d7f' }} />
                                    <span style={{ color: '#c41d7f', fontWeight: 'bold', fontSize: '13px' }}>
                                        Visites Rapprochées (&lt; 5j)
                                    </span>
                                    <Badge count={frequentVisitsAlerts.length} style={{ backgroundColor: '#eb2f96' }} />
                                </Space>
                            }
                            extra={
                                <Button 
                                    size="small" 
                                    icon={<DownloadOutlined />} 
                                    onClick={handleExportFrequentVisits}
                                    disabled={!frequentVisitsAlerts || frequentVisitsAlerts.length === 0}
                                    style={{ borderColor: '#ffadd2', color: '#c41d7f', fontSize: '11px', padding: '0 8px' }}
                                >
                                    Exporter
                                </Button>
                            }
                            style={{ borderRadius: 12, border: '1px solid #ffadd2', background: '#fff0f6', height: '100%' }}
                        >
                            {frequentVisitsAlerts.length > 0 ? (
                                <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
                                    <List
                                        className="mobile-alert-list"
                                        size="small"
                                        dataSource={frequentVisitsAlerts}
                                        renderItem={item => {
                                            const catKey = getCategoryKey(item);
                                            const conf = RELATIONSHIP_CONFIG[catKey] || RELATIONSHIP_CONFIG.STANDARD;

                                            return (
                                                <List.Item
                                                    actions={[
                                                        <Button 
                                                            size="small" 
                                                            type="primary" 
                                                            ghost
                                                            icon={<AuditOutlined />}
                                                            onClick={() => handleOpenDoctorHistory(item.personId, item)}
                                                        >
                                                            Parcours
                                                        </Button>
                                                    ]}
                                                >
                                                    <List.Item.Meta
                                                        avatar={
                                                            <Avatar 
                                                                icon={<UserOutlined />} 
                                                                style={{ backgroundColor: '#ffd6e7', color: '#c41d7f', cursor: 'pointer' }} 
                                                                onClick={() => handleOpenDoctorHistory(item.personId, item)}
                                                            />
                                                        }
                                                        title={
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                                                <Text 
                                                                    strong 
                                                                    style={{ cursor: 'pointer', color: '#1890ff', fontSize: '13px' }} 
                                                                    onClick={() => handleOpenDoctorHistory(item.personId, item)}
                                                                >
                                                                    {item.name}
                                                                </Text>
                                                                <Tag color={conf.tagColor} style={{ fontSize: '10px', fontWeight: 'bold', margin: 0, padding: '0 6px' }}>
                                                                    {conf.icon} {conf.label.toUpperCase()}
                                                                </Tag>
                                                            </div>
                                                        }
                                                        description={
                                                            <div style={{ fontSize: '11px', marginTop: 4 }}>
                                                                <div style={{ marginBottom: 6, display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                                                                    <Tag color="error" style={{ fontSize: '10px', fontWeight: 'bold', margin: 0 }}>
                                                                        ⚠️ {item.totalVisitsCount} visites en {item.totalSpanDays}
                                                                    </Tag>
                                                                    {item.isSameDelegate ? (
                                                                        <Tag color="magenta" style={{ fontSize: '10px', fontWeight: 'bold', margin: 0 }}>
                                                                            {item.delegatesNamesList[0]}
                                                                        </Tag>
                                                                    ) : (
                                                                        <Tag color="purple" style={{ fontSize: '10px', fontWeight: 'bold', margin: 0 }}>
                                                                            👥 {item.uniqueDelegatesCount} délégués
                                                                        </Tag>
                                                                    )}
                                                                </div>

                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                                    {item.visits?.map((v, vIdx) => (
                                                                        <div 
                                                                            key={vIdx} 
                                                                            style={{ 
                                                                                background: '#ffffff', 
                                                                                padding: '6px 8px', 
                                                                                borderRadius: 4, 
                                                                                border: vIdx % 2 === 0 ? '1px solid #ffd6e7' : '1px solid #efdbff' 
                                                                            }}
                                                                        >
                                                                            <div style={{ wordBreak: 'break-word', fontWeight: 600, color: vIdx % 2 === 0 ? '#c41d7f' : '#531dab', fontSize: '10px' }}>
                                                                                V{v.visitIndex} ({v.dateFull}) - {v.delegateName}
                                                                            </div>

                                                                            {v.context ? (
                                                                                <div style={{ marginTop: 4, display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                                                                                    <Tag color="geekblue" style={{ fontSize: '9px', margin: 0 }}>
                                                                                        {v.context.cycleName} - S{v.context.weekNumber}
                                                                                    </Tag>
                                                                                    {v.context.isPlannedThisWeek ? (
                                                                                        <Tag color="green" style={{ fontSize: '9px', margin: 0 }}>✅ Prévu</Tag>
                                                                                    ) : v.context.plannedInOtherWeek ? (
                                                                                        <Tag color="orange" style={{ fontSize: '9px', margin: 0 }}>⚠️ Prévu S{v.context.plannedInOtherWeek}</Tag>
                                                                                    ) : (
                                                                                        <Tag color="default" style={{ fontSize: '9px', margin: 0 }}>❌ Hors planning</Tag>
                                                                                    )}
                                                                                </div>
                                                                            ) : (
                                                                                <Text type="secondary" style={{ fontSize: '10px' }}>Hors cycle actif</Text>
                                                                            )}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        }
                                                    />
                                                </List.Item>
                                            );
                                        }}
                                    />
                                </div>
                            ) : (
                                <Empty description="Aucune visite en doublon (< 5 jours) détectée !" />
                            )}
                        </Card>
                    </Col>
                </Row>
            </Spin>

            {/* --- MODALE HISTORIQUE DES JOURS PRÉCÉDENTS --- */}
            <Modal
                title={
                    <Space>
                        <HistoryOutlined style={{ color: '#1890ff' }} />
                        <span>Historique & Audit d'Activité Journalière</span>
                    </Space>
                }
                open={historyModalVisible}
                onCancel={() => setHistoryModalVisible(false)}
                footer={[
                    <Button key="close" type="primary" onClick={() => setHistoryModalVisible(false)}>
                        Fermer
                    </Button>
                ]}
                width={1000}
            >
                <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 15 }}>
                    <Text strong>Choisir une date à auditer :</Text>
                    <DatePicker 
                        value={historyDate} 
                        onChange={(d) => {
                            setHistoryDate(d);
                            fetchHistoryMonitoring(d);
                        }} 
                        format="DD/MM/YYYY"
                        allowClear={false}
                    />
                    <Button type="primary" icon={<ReloadOutlined />} onClick={() => fetchHistoryMonitoring(historyDate)}>
                        Actualiser
                    </Button>
                </div>

                <Table 
                    dataSource={historyData?.delegates || []}
                    columns={monitoringColumns}
                    rowKey="delegateId"
                    loading={loadingHistory}
                    pagination={false}
                    size="small"
                    scroll={{ y: 400 }}
                />
            </Modal>

            {/* ========================================================================= */}
            {/* ⚡ MODALE PARCOURS DE VISITE & PROFIL DU MÉDECIN (HOMOGÈNE À RAPPORTS) ⚡ */}
            {/* ========================================================================= */}
            <DoctorHistoryModal
                visible={doctorModalVisible}
                onClose={() => setDoctorModalVisible(false)}
                personId={selectedDoctorId}
                zIndex={2000}
            />

            {/* --- MODALE LISTE DES RÉGIONS DU DÉLÉGUÉ CLIQUÉ --- */}
            <Modal
                title={
                    <Space>
                        <EnvironmentOutlined style={{ color: '#1890ff', fontSize: '18px' }} />
                        <span>Régions visitées par : <b style={{ color: '#1890ff' }}>{selectedBarDelegate?.name}</b></span>
                        <Badge count={selectedBarDelegate?.regions?.length || 0} style={{ backgroundColor: '#52c41a' }} />
                    </Space>
                }
                open={!!selectedBarDelegate}
                onCancel={() => setSelectedBarDelegate(null)}
                footer={[
                    <Button key="close" type="primary" onClick={() => setSelectedBarDelegate(null)}>
                        Fermer
                    </Button>
                ]}
                width={650}
            >
                <div style={{ padding: '10px 0' }}>
                    <p style={{ color: '#8c8c8c', marginBottom: 15 }}>
                        Total de <b>{selectedBarDelegate?.realized} visites</b> réalisées sur <b>{selectedBarDelegate?.regions?.length} régions</b>.
                    </p>

                    {selectedBarDelegate?.regions && selectedBarDelegate.regions.length > 0 ? (
                        <div style={{ maxHeight: '350px', overflowY: 'auto', paddingRight: '10px' }}>
                            <div style={{ 
                                display: 'grid', 
                                gridTemplateColumns: 'repeat(2, 1fr)', 
                                gap: '10px' 
                            }}>
                                {selectedBarDelegate.regions.map((regName, idx) => (
                                    <div 
                                        key={idx}
                                        style={{ 
                                            padding: '10px 14px',
                                            background: '#f5f7fa',
                                            border: '1px solid #e4e7ed',
                                            borderRadius: '8px',
                                            fontSize: '13px',
                                            fontWeight: '500',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px'
                                        }}
                                    >
                                        <EnvironmentOutlined style={{ color: '#1890ff' }} />
                                        <span>{typeof regName === 'string' ? regName : regName.name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <Empty description="Aucune région enregistrée pour ce délégué" />
                    )}
                </div>
            </Modal>

            {/* --- MODALE DÉTAIL DES MÉDECINS (MANQUÉS / HORS PORTEFEUILLE) --- */}
           <Modal
                title={<Title level={4} style={{ margin: 0 }}>{modalDetails.title}</Title>}
                open={detailModalVisible}
                onCancel={() => setDetailModalVisible(false)}
                footer={[
                    <Button key="close" type="primary" onClick={() => setDetailModalVisible(false)}>
                        Fermer
                    </Button>
                ]}
                width={850}
                zIndex={1100}
            >
                {modalDetails.currentCycleInfo && (
                    <div style={{ marginBottom: 15, padding: '12px 16px', background: '#fff1f0', border: '1px solid #ffa39e', borderRadius: '8px' }}>
                        <div style={{ marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Text strong style={{ color: '#820014', fontSize: '13px' }}>
                                📅 Cycle en cours ({modalDetails.delegateName}) :
                            </Text>
                            <Tag color="volcano" style={{ fontWeight: 'bold', fontSize: '13px' }}>
                                {modalDetails.currentCycleInfo.cycleName}
                            </Tag>
                        </div>

                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                            {modalDetails.currentCycleInfo.weeks?.map((w, idx) => (
                                <Tag 
                                    key={idx}
                                    color={w.isCurrent ? "error" : "default"}
                                    style={{ 
                                        padding: '4px 10px',
                                        borderRadius: '4px',
                                        fontSize: '11px',
                                        fontWeight: w.isCurrent ? 'bold' : 'normal',
                                        border: w.isCurrent ? '2px solid #cf1322' : '1px solid #d9d9d9',
                                        background: w.isCurrent ? '#ffccc7' : '#fafafa'
                                    }}
                                >
                                    <b>Semaine {w.weekNumber}</b> ({w.startShort} au {w.endShort})
                                    {w.isCurrent ? ' 🔴 (Semaine de la visite)' : ''}
                                </Tag>
                            ))}
                        </div>
                    </div>
                )}
                <List
                    itemLayout="horizontal"
                    dataSource={modalDetails.list}
                    locale={{ emptyText: "Aucun médecin dans cette liste." }}
                    renderItem={(item) => {
                        const catKey = getCategoryKey(item);
                        const conf = RELATIONSHIP_CONFIG[catKey] || RELATIONSHIP_CONFIG.STANDARD;

                        return (
                            <List.Item
                                actions={[
                                    <Button 
                                        size="small" 
                                        icon={<AuditOutlined />} 
                                        onClick={() => handleOpenDoctorHistory(item.id, item)}
                                    >
                                        Parcours
                                    </Button>
                                ]}
                            >
                                <List.Item.Meta
                                    avatar={
                                        <Avatar 
                                            icon={<UserOutlined />} 
                                            style={{ 
                                                backgroundColor: modalDetails.type === 'missed' ? '#fff1f0' : '#fffbe6', 
                                                color: modalDetails.type === 'missed' ? '#f5222d' : '#faad14' 
                                            }} 
                                        />
                                    }
                                    title={
                                        <Space wrap align="center">
                                            <Text strong style={{ fontSize: '14px' }}>{item.name}</Text>
                                            <Tag color={conf.tagColor} style={{ fontWeight: 'bold', fontSize: '11px', margin: 0 }}>
                                                {conf.icon} {conf.label.toUpperCase()}
                                            </Tag>
                                        </Space>
                                    }
                                    description={
                                        <Space direction="vertical" size={4} style={{ width: '100%', marginTop: 4 }}>
                                            <Space wrap>
                                                <Tag color="blue">{item.specialty}</Tag>
                                                <Tag color="cyan"><EnvironmentOutlined /> {item.region}</Tag>
                                                {item.visitDate && (
                                                    <Tag color="geekblue" icon={<CalendarOutlined />}>
                                                        Visité le : {item.visitDate}
                                                    </Tag>
                                                )}
                                            </Space>

                                            {modalDetails.type === 'unassigned' && (
                                                <div style={{ marginTop: 6, marginBottom: 6 }}>
                                                    <Text strong style={{ fontSize: '12px', color: '#595959' }}>
                                                        Planification dans {modalDetails.currentCycleInfo?.cycleName ? `le ${modalDetails.currentCycleInfo.cycleName}` : 'ce cycle'} :{' '}
                                                    </Text>
                                                    {item.currentCycleSchedule && item.currentCycleSchedule.length > 0 ? (
                                                        <Space wrap size={[4, 4]}>
                                                            {item.currentCycleSchedule.map((s, idx) => (
                                                                <Tag 
                                                                    key={idx} 
                                                                    color="volcano" 
                                                                    style={{ fontWeight: 'bold', fontSize: '12px', border: '1px solid #ff7a45' }}
                                                                >
                                                                    ⚠️ Prévu en Semaine {s.weekNumber} (du {s.startDate} au {s.endDate})
                                                                </Tag>
                                                            ))}
                                                        </Space>
                                                    ) : (
                                                        <Tag color="orange" style={{ fontSize: '11px', fontWeight: 'bold' }}>
                                                            ❌ Non planifié dans ce cycle (Visite 100% hors planning)
                                                        </Tag>
                                                    )}
                                                </div>
                                            )}

                                            {modalDetails.type === 'unassigned' && (
                                                <div style={{ marginTop: 6 }}>
                                                    {item.plannedSchedules && item.plannedSchedules.length > 0 ? (
                                                        (() => {
                                                            const sortedSchedules = [...item.plannedSchedules].sort((a, b) => {
                                                                const numA = parseInt(String(a.cycleName).replace(/\D/g, ''), 10) || 0;
                                                                const numB = parseInt(String(b.cycleName).replace(/\D/g, ''), 10) || 0;
                                                                if (numA !== numB) return numA - numB;
                                                                return (a.rawStart || 0) - (b.rawStart || 0);
                                                            });

                                                            return (
                                                                <Collapse
                                                                    ghost
                                                                    size="small"
                                                                    style={{ 
                                                                        background: '#fffbf0', 
                                                                        border: '1px solid #ffd591', 
                                                                        borderRadius: '6px' 
                                                                    }}
                                                                    items={[
                                                                        {
                                                                            key: 'schedules_list',
                                                                            label: (
                                                                                <Space>
                                                                                    <CalendarOutlined style={{ color: '#d46b08' }} />
                                                                                    <Text strong style={{ color: '#d46b08', fontSize: '12px' }}>
                                                                                        Planification prévue pour ce délégué (toute l'année)
                                                                                    </Text>
                                                                                    <Badge 
                                                                                        count={sortedSchedules.length} 
                                                                                        style={{ backgroundColor: '#fa8c16' }} 
                                                                                    />
                                                                                </Space>
                                                                            ),
                                                                            children: (
                                                                                <div style={{ 
                                                                                    display: 'grid', 
                                                                                    gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))', 
                                                                                    gap: '6px',
                                                                                    maxHeight: '180px',
                                                                                    overflowY: 'auto',
                                                                                    padding: '4px'
                                                                                }}>
                                                                                    {sortedSchedules.map((s, idx) => (
                                                                                        <Tag 
                                                                                            key={idx} 
                                                                                            color="volcano" 
                                                                                            style={{ 
                                                                                                padding: '4px 8px', 
                                                                                                margin: 0, 
                                                                                                fontSize: '11px', 
                                                                                                display: 'flex',
                                                                                                justifyContent: 'space-between',
                                                                                                alignItems: 'center',
                                                                                                border: '1px solid #ffbb96'
                                                                                            }}
                                                                                        >
                                                                                            <span>📅 <b>{s.cycleName.toUpperCase()}</b> - S{s.weekNumber}</span>
                                                                                            <span style={{ fontSize: '10px', color: '#595959', marginLeft: 8 }}>
                                                                                                ({s.startDate} au {s.endDate})
                                                                                            </span>
                                                                                        </Tag>
                                                                                    ))}
                                                                                </div>
                                                                            )
                                                                        }
                                                                    ]}
                                                                />
                                                            );
                                                        })()
                                                    ) : (
                                                        <Tag color="orange" style={{ fontSize: '11px' }}>
                                                            ❌ Non planifié dans aucun cycle de ce délégué
                                                        </Tag>
                                                    )}
                                                </div>
                                            )}

                                            {modalDetails.type === 'unassigned' && item.officialDelegates && item.officialDelegates.length > 0 && (
                                                <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginTop: 2 }}>
                                                    Assigné au portefeuille de : <b style={{ color: '#1890ff' }}>{item.officialDelegates.join(', ')}</b>
                                                </Text>
                                            )}
                                        </Space>
                                    }
                                />
                            </List.Item>
                        );
                    }}
                />
            </Modal>

            {/* --- MODALE DÉTAIL DES MÉDECINS VISITÉS HORS PLANNING (RÉGION) --- */}
            <Modal
                title={
                    <Space>
                        <EnvironmentOutlined style={{ color: '#f5222d', fontSize: '20px' }} />
                        <Title level={4} style={{ margin: 0 }}>
                            Médecins visités hors planning à <span style={{ color: '#f5222d' }}>{selectedOffPlanInfo.region.toUpperCase()}</span>
                        </Title>
                    </Space>
                }
                open={offPlanModalVisible}
                onCancel={() => setOffPlanModalVisible(false)}
                footer={[
                    <Button key="close" type="primary" onClick={() => setOffPlanModalVisible(false)}>
                        Fermer
                    </Button>
                ]}
                width={850}
                zIndex={1100}
            >
                <div style={{ marginBottom: 15, padding: '12px 16px', background: '#fff1f0', border: '1px solid #ffa39e', borderRadius: '8px' }}>
                    <Row gutter={[16, 8]} align="middle">
                        <Col span={12}>
                            <Text strong>Délégué : </Text>
                            <Text style={{ color: '#cf1322', fontWeight: 'bold', fontSize: '14px' }}>{selectedOffPlanInfo.delegateName}</Text>
                        </Col>
                        <Col span={12} style={{ textAlign: 'right' }}>
                            <Text type="secondary">
                                Total : <b style={{ color: '#cf1322' }}>{selectedOffPlanInfo.doctors.length}</b> médecin(s) visité(s) hors planning
                            </Text>
                        </Col>

                        <Col span={24} style={{ marginTop: 6, paddingTop: 8, borderTop: '1px dashed #ffa39e' }}>
                            {selectedOffPlanInfo.currentCycleInfo ? (
                                <div>
                                    <div style={{ marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <Text strong style={{ color: '#820014', fontSize: '13px' }}>
                                            📅 Cycle en cours :
                                        </Text>
                                        <Tag color="volcano" style={{ fontWeight: 'bold', fontSize: '13px' }}>
                                            {selectedOffPlanInfo.currentCycleInfo.cycleName}
                                        </Tag>
                                    </div>

                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>
                                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                            {selectedOffPlanInfo.currentCycleInfo.weeks?.filter(w => !w.isCurrent).map((w, idx) => (
                                                <Tag 
                                                    key={idx}
                                                    style={{ 
                                                        padding: '3px 8px',
                                                        borderRadius: '4px',
                                                        fontSize: '11px',
                                                        border: '1px solid #d9d9d9',
                                                        background: '#fafafa',
                                                        color: '#595959'
                                                    }}
                                                >
                                                    Semaine {w.weekNumber} ({w.startShort} au {w.endShort})
                                                </Tag>
                                            ))}
                                        </div>

                                        {selectedOffPlanInfo.currentCycleInfo.weeks?.filter(w => w.isCurrent).map((w, idx) => (
                                            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                                <Tag 
                                                    color="error"
                                                    style={{ 
                                                        padding: '4px 10px',
                                                        borderRadius: '4px',
                                                        fontSize: '11px',
                                                        fontWeight: 'bold',
                                                        border: '2px solid #cf1322',
                                                        background: '#ffccc7',
                                                        margin: 0
                                                    }}
                                                >
                                                    Semaine {w.weekNumber} ({w.startShort} au {w.endShort}) 🔴 (Semaine de la visite)
                                                </Tag>

                                                <div style={{ 
                                                    display: 'inline-flex', 
                                                    alignItems: 'center', 
                                                    gap: 6, 
                                                    background: '#ffffff', 
                                                    padding: '3px 10px', 
                                                    borderRadius: '4px',
                                                    border: '1px solid #91caff'
                                                }}>
                                                    <Text strong style={{ color: '#003a8c', fontSize: '11px' }}>
                                                        📍 Régions prévues cette semaine :
                                                    </Text>
                                                    {selectedOffPlanInfo.currentCycleInfo.plannedRegionsForCurrentWeek && selectedOffPlanInfo.currentCycleInfo.plannedRegionsForCurrentWeek.length > 0 ? (
                                                        selectedOffPlanInfo.currentCycleInfo.plannedRegionsForCurrentWeek.map((reg, rIdx) => (
                                                            <Tag key={rIdx} color="blue" style={{ fontWeight: 'bold', fontSize: '11px', margin: 0 }}>
                                                                {reg}
                                                            </Tag>
                                                        ))
                                                    ) : (
                                                        <Text type="secondary" style={{ fontSize: '11px' }}>Aucune région définie</Text>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <Tag color="orange">Aucun cycle actif trouvé pour ce délégué à cette date.</Tag>
                            )}
                        </Col>
                    </Row>
                </div>

                <List
                    itemLayout="horizontal"
                    dataSource={selectedOffPlanInfo.doctors}
                    locale={{ emptyText: "Aucun médecin enregistré pour cette région." }}
                    renderItem={(item) => {
                        const catKey = getCategoryKey(item);
                        const conf = RELATIONSHIP_CONFIG[catKey] || RELATIONSHIP_CONFIG.STANDARD;

                        return (
                            <List.Item
                                actions={[
                                    <Button 
                                        size="small" 
                                        type="primary" 
                                        ghost
                                        icon={<AuditOutlined />} 
                                        onClick={() => handleOpenDoctorHistory(item.id, item)}
                                    >
                                        Parcours
                                    </Button>
                                ]}
                            >
                                <List.Item.Meta
                                    avatar={
                                        <Avatar 
                                            icon={<UserOutlined />} 
                                            style={{ backgroundColor: '#ffccc7', color: '#f5222d' }} 
                                        />
                                    }
                                    title={
                                        <Space wrap align="center">
                                            <Text strong style={{ fontSize: '14px' }}>{item.name}</Text>
                                            <Tag color={conf.tagColor} style={{ fontWeight: 'bold', fontSize: '11px', margin: 0 }}>
                                                {conf.icon} {conf.label.toUpperCase()}
                                            </Tag>
                                            <Tag color="blue" icon={<CalendarOutlined />}>
                                                Visité le {item.visitFullDate || `${item.visitDate} à ${item.visitTime}`}
                                            </Tag>
                                        </Space>
                                    }
                                    description={
                                        <Space direction="vertical" size={4} style={{ marginTop: 4 }}>
                                            <Space>
                                                <Tag color="blue">{item.specialty}</Tag>
                                                <Tag color="cyan"><EnvironmentOutlined /> {item.region}</Tag>
                                            </Space>

                                            <div style={{ marginTop: 4 }}>
                                                <Text strong style={{ fontSize: '12px', color: '#595959' }}>
                                                    Planification dans ce cycle :{' '}
                                                </Text>
                                                {item.currentCycleSchedule && item.currentCycleSchedule.length > 0 ? (
                                                    <Space wrap size={[4, 4]}>
                                                        {item.currentCycleSchedule.map((s, idx) => (
                                                            <Tag 
                                                                key={idx} 
                                                                color={s.isCurrentWeek ? "green" : "volcano"} 
                                                                style={{ fontWeight: 'bold', fontSize: '12px' }}
                                                            >
                                                                ⚠️ Prévu en Semaine {s.weekNumber} (du {s.startDate} au {s.endDate})
                                                            </Tag>
                                                        ))}
                                                    </Space>
                                                ) : (
                                                    <Tag color="orange" style={{ fontSize: '11px' }}>
                                                        ❌ Non planifié dans ce cycle (Visite 100% hors planning)
                                                    </Tag>
                                                )}
                                            </div>
                                        </Space>
                                    }
                                />
                            </List.Item>
                        );
                    }}
                />
            </Modal>

            {/* --- MODALE HISTORIQUE DES SEMAINES PRÉCÉDENTES --- */}
            <Modal
                title={
                    <Space>
                        <HistoryOutlined style={{ color: '#1890ff', fontSize: '18px' }} />
                        <Title level={4} style={{ margin: 0 }}>
                            Historique des Semaines : Audit & Contrôle Hebdomadaire
                        </Title>
                    </Space>
                }
                open={prevWeekHistoryModalVisible}
                onCancel={() => setPrevWeekHistoryModalVisible(false)}
                footer={[
                    <Button key="close" type="primary" onClick={() => setPrevWeekHistoryModalVisible(false)}>
                        Fermer
                    </Button>
                ]}
                width={1050}
            >
                <div style={{ marginBottom: 20, padding: '12px 16px', background: '#f5f7fa', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                    <Space align="center" wrap>
                        <Text strong>Choisir la semaine à auditer :</Text>
                        <RangePicker 
                            value={historyWeekRange} 
                            onChange={(dates) => {
                                setHistoryWeekRange(dates);
                                fetchHistoryWeekCompliance(dates);
                            }} 
                            format="DD/MM/YYYY"
                            allowClear={false}
                        />
                        <Button type="primary" icon={<ReloadOutlined />} onClick={() => fetchHistoryWeekCompliance(historyWeekRange)}>
                            Charger
                        </Button>
                    </Space>

                    {historyWeekData?.periodLabel && (
                        <Tag color="blue" style={{ fontSize: '12px', padding: '4px 10px', fontWeight: 'bold' }}>
                            📅 Période : {historyWeekData.periodLabel}
                        </Tag>
                    )}
                </div>

                <Table 
                    dataSource={historyWeekData?.delegates || []}
                    columns={prevWeekColumns}
                    rowKey="delegateId"
                    loading={loadingHistoryWeek}
                    pagination={false}
                    size="middle"
                    scroll={{ y: 400 }}
                    locale={{ emptyText: "Aucune donnée disponible pour cette semaine." }}
                />
            </Modal>

            {/* --- MODALE VISITES SUPERVISEURS --- */}
            <Modal
                title={
                    <Space>
                        <CrownOutlined style={{ color: '#722ed1', fontSize: '20px' }} />
                        <Title level={4} style={{ margin: 0 }}>
                            Visites de Contrôle & Accompagnement : <span style={{ color: '#722ed1' }}>{selectedSupervisorVisits.delegateName}</span>
                        </Title>
                    </Space>
                }
                open={supervisorModalVisible}
                onCancel={() => setSupervisorModalVisible(false)}
                footer={[
                    <Button key="close" type="primary" onClick={() => setSupervisorModalVisible(false)}>
                        Fermer
                    </Button>
                ]}
                width={800}
                zIndex={1100}
            >
                <List
                    itemLayout="vertical"
                    dataSource={selectedSupervisorVisits.visits}
                    locale={{ emptyText: "Aucune visite de supervision trouvée." }}
                    renderItem={(visit, idx) => {
                        const isCreation = (visit.controlType || '').toUpperCase().includes('CREATION') || (visit.controlType || '').toUpperCase().includes('CRÉATION');

                        return (
                            <List.Item
                                key={idx}
                                style={{ 
                                    background: '#fafafa', 
                                    border: '1px solid #d3adf7', 
                                    borderRadius: '8px', 
                                    marginBottom: 12, 
                                    padding: '14px 18px' 
                                }}
                            >
                                <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
                                    <Col>
                                        <Space wrap>
                                            <MedicineBoxOutlined style={{ color: '#1890ff', fontSize: '16px' }} />
                                            <Text strong style={{ fontSize: '15px' }}>{visit.doctorName}</Text>
                                            <Tag color="cyan">{visit.region}</Tag>
                                            <Tag color="blue">{visit.specialty}</Tag>
                                        </Space>
                                    </Col>
                                    <Col>
                                        <Space>
                                            {visit.rating && <Tag color="gold" style={{ fontWeight: 'bold' }}>⭐ {visit.rating}</Tag>}
                                            <Tag color={isCreation ? "green" : "purple"} style={{ fontWeight: 'bold' }}>
                                                {isCreation ? "✨ Création" : (visit.controlType || 'Accompagnement')}
                                            </Tag>
                                        </Space>
                                    </Col>
                                </Row>

                                <div style={{ marginBottom: 8, fontSize: '12px', color: '#595959' }}>
                                    📅 <b>Date :</b> {visit.date} | 👑 <b>Superviseur :</b> <span style={{ color: '#722ed1', fontWeight: 'bold' }}>{visit.supervisorName}</span>
                                    {visit.delegateName && (
                                        <>
                                            {' | '} 👤 <b>Délégué(s) accompagné(s) :</b> <span style={{ color: '#096dd9', fontWeight: 'bold' }}>{visit.delegateName}</span>
                                        </>
                                    )}
                                </div>

                                {(visit.doctorPotential || visit.productPotential) && (
                                    <div style={{ 
                                        background: '#f6ffed', 
                                        border: '1px solid #b7eb8f', 
                                        borderRadius: 6, 
                                        padding: '10px 14px', 
                                        marginBottom: 10,
                                        marginTop: 6
                                    }}>
                                        <Text strong style={{ color: '#389e0d', display: 'block', marginBottom: 4, fontSize: '12px' }}>
                                            🎯 Qualification de la Création :
                                        </Text>
                                        <Space size={20} wrap>
                                            {visit.doctorPotential && (
                                                <span>
                                                    <strong>Potentiel Médecin :</strong>{' '}
                                                    <Tag color="green" style={{ fontWeight: 'bold', fontSize: '12px' }}>
                                                        {visit.doctorPotential}
                                                    </Tag>
                                                </span>
                                            )}
                                            {visit.productPotential && (
                                                <span>
                                                    <strong>Potentiel Produit :</strong>{' '}
                                                    <Tag color="cyan" style={{ fontWeight: 'bold', fontSize: '12px' }}>
                                                        {visit.productPotential}
                                                    </Tag>
                                                </span>
                                            )}
                                        </Space>
                                    </div>
                                )}

                                {visit.comment && (
                                    <div style={{ 
                                        background: '#ffffff', 
                                        padding: '10px 14px', 
                                        borderRadius: '6px', 
                                        border: '1px dashed #d3adf7',
                                        marginTop: 6
                                    }}>
                                        <Text strong style={{ color: '#722ed1', display: 'block', marginBottom: 4 }}>
                                            📝 Observations du Superviseur :
                                        </Text>
                                        <Text italic style={{ color: '#262626' }}>
                                            « {visit.comment} »
                                        </Text>
                                    </div>
                                )}
                            </List.Item>
                        );
                    }}
                />
            </Modal>

        </div>
    );
};

export default DashboardPage;